import React from "react";
import { Box, Button, Flex, Link, Text, Tooltip } from "@chakra-ui/react";
import SVG from "react-inlinesvg";
import useGetMyDashboardLineSheetByBrand from "@/features/my-dashboard/hooks/useGetMyDashboardLineSheetByBrand";
import useIdentity from "@/features/ui/hooks/use-identity";
import { useRecoilValue } from "recoil";
import { myDashboardActiveBrandStore } from "@/features/my-dashboard/stores/my-dashboard-active-brand-store";
import { MyDashboardLineSheetByBrandData } from "@/features/my-dashboard/types";
import usePostBrandRequest from "@/shared/hooks/buyer-requests/usePostBrandRequest";
import useI18nHelper from "@/features/ui/hooks/use-i18n-helper";
import { Trans } from "react-i18next";

function MyDashboardLineSheetList() {
  const identity = useIdentity();
  const activeBrand = useRecoilValue(myDashboardActiveBrandStore);

  const { myDashboardLineSheetByBrand } = useGetMyDashboardLineSheetByBrand(
    identity?.company?.id,
    activeBrand?.brandId
  );

  const top5LineSheetList = myDashboardLineSheetByBrand?.slice(0, 5);
  const { tSentence } = useI18nHelper();

  return (
    <Flex
      flexDirection={"column"}
      marginTop={"24px"}
      sx={{
        gap: "12px",
      }}
    >
      <Flex gap={"2px"} alignItems={"center"}>
        <Text textStyle={"body-lg-bold"}>
          <Trans
            i18nKey={"my_dash:line_sheet_list_title"}
            values={{
              brand: activeBrand.brandName,
            }}
            components={{
              1: <Text as={"span"} color={"#1272EF"} />,
            }}
          />
          {/*<span style={{ color: "#1272EF" }}>{activeBrand.brandName} </span>*/}
          {/*라인시트 최다 발행사 TOP 5*/}
        </Text>
        <Tooltip
          label={
            <Flex
              textStyle={"body-sm-medium"}
              flexDirection={"column"}
              gap={"12px"}
            >
              <Text>{tSentence("my_dash:line_sheet_list_tooltip")}</Text>
            </Flex>
          }
          hasArrow={true}
          placement={"top"}
          openDelay={500}
        >
          <Box>
            <SVG
              src={"/icons-v1_1/info-circle.svg"}
              width={20}
              height={20}
              color={"#8F8F8C"}
            />
          </Box>
        </Tooltip>
      </Flex>
      <Flex gap={"16px"}>
        {top5LineSheetList?.length === 0 && <LineSheetNull />}
        {top5LineSheetList?.map((lineSheet, _, arr) => (
          <MyDashboardLineSheetCard
            key={lineSheet.rank}
            lineSheetData={lineSheet}
            lineSheetListLength={arr.length}
            activeBrandId={activeBrand.brandId}
          />
        ))}
      </Flex>
    </Flex>
  );
}

export default MyDashboardLineSheetList;

interface MyDashboardLineSheetCardProps {
  lineSheetData: MyDashboardLineSheetByBrandData;
  lineSheetListLength: number;
  activeBrandId?: number;
}

const MyDashboardLineSheetCard = ({
  lineSheetData,
  lineSheetListLength,
  activeBrandId,
}: MyDashboardLineSheetCardProps) => {
  const getLineSheetCount = (type: "STOCK" | "SEASON" | "API") =>
    lineSheetData.line_sheet_set_types.find(
      (item) => item.line_sheet_set_type === type
    )?.count ?? 0;
  const { tSentence } = useI18nHelper();

  return (
    <Flex
      padding={"16px"}
      paddingRight={"12px"}
      flexDirection={"column"}
      boxShadow={"0 2px 4px 0 rgba(0, 0, 0, 0.10)"}
      position={"relative"}
      borderRadius={"8px"}
      background={"#FFFFFF"}
      gap={"8px"}
      sx={{
        maxWidth: lineSheetListLength <= 4 ? "20%" : "undefined",
        flex: 1,
        "&::before": {
          content: '""',
          position: "absolute",
          inset: 0,
          boxShadow: "0 0 0 0.5px #D4D4D1",
          borderRadius: "8px",
          pointerEvents: "none",
          zIndex: 1,
        },
        "&:hover::before": {
          boxShadow: "0 0 0 0.5px #1272EF",
        },
      }}
    >
      <Flex gap={"8px"} color={"#444444"}>
        <Text textStyle={"body-lg-bold"}>{lineSheetData.rank}</Text>
        <Text textStyle={"body-lg-bold"}>{lineSheetData.boutique_name}</Text>
      </Flex>
      <Flex flexDirection={"column"} gap={"6px"}>
        <Flex justifyContent={"space-between"} alignItems={"center"}>
          <Text textStyle={"body-sm-regular"} color={"#6F6F6C"}>
            {tSentence("my_dash:line_sheet_pre_order")}
          </Text>
          <Link
            display={"flex"}
            alignItems={"center"}
            cursor={"pointer"}
            _hover={{
              "& > p,svg": {
                color: "#1272EF",
              },
            }}
            _active={{
              "& > p,svg": {
                color: "#00417D",
              },
            }}
            href={`/line-sheets?type__eq=SEASON&made_by_company.id__in=${activeBrandId}&page_number=1&issued_by_company.id__in=${lineSheetData.boutique_id}`}
          >
            <Text textStyle={"body-sm-medium"} color={"#363636"}>
              {getLineSheetCount("SEASON")}개
            </Text>
            <SVG
              src={"/icons-v1_1/chevron-right.svg"}
              width={24}
              height={24}
              color={"#8F8F8C"}
              style={{
                transform: "translateY(1px)",
              }}
            />
          </Link>
        </Flex>
        <Flex justifyContent={"space-between"} alignItems={"center"}>
          <Text textStyle={"body-sm-regular"} color={"#6F6F6C"}>
            {tSentence("my_dash:line_sheet_stock_order")}
          </Text>
          <Link
            display={"flex"}
            alignItems={"center"}
            cursor={"pointer"}
            _hover={{
              "& > p,svg": {
                color: "#1272EF",
              },
            }}
            _active={{
              "& > p,svg": {
                color: "#00417D",
              },
            }}
            href={`/line-sheets?type__eq=STOCK&made_by_company.id__in=${activeBrandId}&page_number=1&issued_by_company.id__in=${lineSheetData.boutique_id}`}
          >
            <Text textStyle={"body-sm-medium"} color={"#363636"}>
              {getLineSheetCount("STOCK")}개
            </Text>
            <SVG
              src={"/icons-v1_1/chevron-right.svg"}
              width={24}
              height={24}
              color={"#8F8F8C"}
              style={{
                transform: "translateY(1px)",
              }}
            />
          </Link>
        </Flex>
      </Flex>
    </Flex>
  );
};

const LineSheetNull = () => {
  const identity = useIdentity();
  const activeBrand = useRecoilValue(myDashboardActiveBrandStore);

  const { postBrandRequest } = usePostBrandRequest(activeBrand.brandName!);
  return (
    <Flex
      flexDirection={"column"}
      gap={"6px"}
      justifyContent={"center"}
      alignItems={"center"}
      width={"100%"}
      border={"1px dashed #8F8F8C"}
      padding={"20px 12px"}
      borderRadius={"8px"}
    >
      <Box textAlign={"center"} textStyle={"body-md-regular"} color={"#6F6F6C"}>
        <Text>선택한 브랜드를 취급하는 공급사가 없습니다.</Text>
        <Text>요청을 남겨주시면 신속하게 공급사를 탐색하겠습니다.</Text>
      </Box>
      <Button
        padding={"4px 8px"}
        borderRadius={"4px"}
        background={"#FCFCFB"}
        height={"28px"}
        boxShadow={" 0 2px 4px 0 rgba(0, 0, 0, 0.10)"}
        onClick={() => {
          if (!identity?.company?.id) return;
          postBrandRequest(identity.company.id);
        }}
        _before={{
          position: "absolute",
          inset: 0,
          content: "''",
          pointerEvents: "none",
          borderRadius: "4px",
          boxShadow: "0 0 0 0.5px #8F8F8C",
          zIndex: 1,
        }}
      >
        <Text textStyle={"body-sm-bold"}>요청하기</Text>
      </Button>
    </Flex>
  );
};
